import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@legal-500/legal500-design-system";
import { firms } from "@/api/firms.service";
import type { Firm } from "@/api/firms.service";
import styles from "../../../pages/Profiles/ProfilesForm.module.css";

export type CompanyPickerProps = {
  value: string | null;
  onChange: (companyId: string | null, name?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  businessType?: string;
};

const DEBOUNCE_MS = 300;

export function CompanyPicker({
  value,
  onChange,
  disabled,
  placeholder = "Search firms...",
  label = "Company",
  businessType,
}: CompanyPickerProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<Firm[]>([]);
  const [resolvedName, setResolvedName] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Tracks whether a search for the current query has completed, so we can
  // distinguish "no results yet" from "search ran and matched nothing".
  const [hasSearched, setHasSearched] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Search
  useEffect(() => {
    if (!debouncedQuery.trim() || disabled) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    firms
      .getAll({ search: debouncedQuery, limit: 10, businessType })
      .then(resp => {
        if (!cancelled) setResults(resp.companies);
      })
      .catch(err => {
        // Search failure shouldn't break the picker — surface to dev console
        // so the failure is debuggable without disrupting the UI.
        console.error("[CompanyPicker] firm search failed", debouncedQuery, err);
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setHasSearched(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, disabled, businessType]);

  // Resolve current value name. `value` is an address-book UUID.
  useEffect(() => {
    if (!value) {
      setResolvedName(null);
      return;
    }
    let cancelled = false;
    firms.getByUuid(value).then(firm => {
      if (!cancelled) setResolvedName(firm?.name ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);

  // Close on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const displayValue = useMemo(() => {
    if (open) return query;
    if (value && resolvedName) return resolvedName;
    if (value) return `Firm ${value}`;
    return query;
  }, [open, query, value, resolvedName]);

  return (
    <div ref={containerRef} className={styles.comboboxContainer}>
      <Input
        label={label}
        type="text"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        aria-label={label}
        role="combobox"
        aria-expanded={open}
        fullWidth
      />
      {value && !disabled && (
        <button
          type="button"
          aria-label="Clear company"
          className={styles.inlineClear}
          onClick={() => {
            onChange(null);
            setQuery("");
            setResolvedName(null);
          }}
        >
          ×
        </button>
      )}
      {open && !disabled && (results.length > 0 || loading || hasSearched) && (
        <ul role="listbox" className={styles.comboboxList}>
          {loading && <li className={styles.comboboxStatus}>Searching…</li>}
          {!loading && hasSearched && results.length === 0 && (
            <li className={styles.comboboxStatus} aria-live="polite">
              No matching companies
            </li>
          )}
          {!loading &&
            results.map(firm => (
              <li
                key={firm.id}
                role="option"
                aria-selected={firm.id === value}
                tabIndex={0}
                onClick={() => {
                  onChange(firm.id, firm.name);
                  setQuery("");
                  setOpen(false);
                  setResolvedName(firm.name);
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onChange(firm.id, firm.name);
                    setQuery("");
                    setOpen(false);
                    setResolvedName(firm.name);
                  }
                }}
                className={styles.comboboxItem}
              >
                {firm.name}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
